import './style.css';
import { TraceTail } from '@tracetail/js';
import { createCodeBlock } from './components/CodeBlock.js';

// Count all individual data points in components (not just top-level categories)
function countAllSignalDataPoints(obj, visited = new WeakSet()) {
  if (!obj || typeof obj !== 'object') return 0;

  // Prevent infinite recursion
  if (visited.has(obj)) return 0;
  visited.add(obj);

  // Arrays: count each element individually (e.g., array of 13 fonts = 13 data points)
  if (Array.isArray(obj)) {
    return obj.reduce((sum, item) => {
      if (typeof item === 'object' && item !== null) {
        return sum + countAllSignalDataPoints(item, visited);
      }
      return sum + 1; // Each primitive in array counts as 1
    }, 0);
  }

  // Objects: count all leaf properties recursively
  let count = 0;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue; // Skip null/undefined
      } else if (typeof value === 'object') {
        count += countAllSignalDataPoints(value, visited);
      } else {
        count += 1; // Primitive value = 1 data point
      }
    }
  }

  return count;
}

// Initialize TraceTail with server-side processing
const tracetail = new TraceTail({
  apiKey: 'demo_key_for_showcase',
  endpoint: 'https://tracetail.io/api', // Server-side processing
  caching: true,
  debug: true // Enable to see server integration
});

// DOM elements
const regenerateBtn = document.getElementById('regenerateBtn');
const statusBar = document.getElementById('statusBar');
const fingerprintDisplay = document.getElementById('fingerprintDisplay');
const visitorIdEl = document.getElementById('visitorId');
const confidenceEl = document.getElementById('confidence');
const confidenceBar = document.getElementById('confidenceBar');
const processingTimeEl = document.getElementById('processingTime');
const componentCountEl = document.getElementById('componentCount');
const browserSignalsEl = document.getElementById('browserSignals');

// Update status bar
function updateStatus(status, message) {
  const statusClasses = {
    loading: 'bg-yellow-50 border-yellow-200',
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200'
  };

  statusBar.className = `px-6 py-4 border-b ${statusClasses[status]}`;

  const statusIcons = {
    loading: '<div class="loading-spinner"></div>',
    success: '<svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>',
    error: '<svg class="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
  };

  const textColors = {
    loading: 'text-yellow-800',
    success: 'text-green-800',
    error: 'text-red-800'
  };

  statusBar.innerHTML = `
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-3">
        ${statusIcons[status]}
        <span class="${textColors[status]} font-medium">${message}</span>
      </div>
      <button id="regenerateBtn" ${status === 'loading' ? 'disabled' : ''} class="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all ${status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''}">
        <svg class="h-4 w-4 ${status === 'loading' ? 'animate-spin' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>${status === 'loading' ? 'Generating...' : 'Regenerate'}</span>
      </button>
    </div>
  `;

  // Re-attach event listener
  const newBtn = document.getElementById('regenerateBtn');
  newBtn.addEventListener('click', generateFingerprint);
}

// Display fingerprint data
function displayFingerprint(fingerprint) {
  // Show display section
  fingerprintDisplay.classList.remove('hidden');

  // Update visitor ID
  visitorIdEl.textContent = fingerprint.visitorId;

  // Update confidence
  const confidencePercent = (fingerprint.confidence * 100).toFixed(1);
  confidenceEl.textContent = `${confidencePercent}%`;
  confidenceBar.style.width = `${confidencePercent}%`;

  // Update processing time
  processingTimeEl.textContent = `${fingerprint.processingTime}ms`;

  // Update component count
  const componentCount = fingerprint.components ? countAllSignalDataPoints(fingerprint.components) : '';
  componentCountEl.textContent = componentCount;

  // Update browser signals
  const signals = [
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>',
      label: 'Screen Resolution',
      value: `${screen.width}x${screen.height}`
    },
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
      label: 'User Agent',
      value: 'Detected'
    },
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>',
      label: 'Canvas Fingerprint',
      value: 'Generated'
    },
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>',
      label: 'WebGL Renderer',
      value: 'Detected'
    },
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
      label: 'Timezone',
      value: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    {
      icon: '<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>',
      label: 'Platform',
      value: navigator.platform
    }
  ];

  browserSignalsEl.innerHTML = signals.map(signal => `
    <div class="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
      ${signal.icon}
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium text-gray-700">${signal.label}</div>
        <div class="text-xs text-gray-500 truncate">${signal.value}</div>
      </div>
    </div>
  `).join('');
}

// Generate fingerprint
async function generateFingerprint() {
  try {
    updateStatus('loading', 'Generating fingerprint...');

    const fingerprint = await tracetail.generateFingerprint({ verbose: true });

    updateStatus('success', 'Fingerprint generated successfully');
    displayFingerprint(fingerprint);
  } catch (error) {
    updateStatus('error', `Error: ${error.message}`);
    console.error('Fingerprint generation failed:', error);
  }
}

// Initial setup
regenerateBtn.addEventListener('click', generateFingerprint);

// Auto-generate on load
window.addEventListener('load', () => {
  // Give UI a moment to settle
  setTimeout(generateFingerprint, 100);

  // Replace installation block with CodeBlock
  const installationContainer = document.getElementById('installationBlock');
  if (installationContainer) {
    const installCode = `npm install @tracetail/js
# or
yarn add @tracetail/js
# or
pnpm add @tracetail/js`;
    installationContainer.innerHTML = '';
    installationContainer.appendChild(createCodeBlock(installCode, 'bash', 'Installation'));
  }

  // Replace code example with CodeBlock
  const codeExampleContainer = document.getElementById('codeExampleBlock');
  if (codeExampleContainer) {
    const exampleCode = `import { TraceTail } from '@tracetail/js';

// Initialize TraceTail
const tracetail = new TraceTail({
  apiKey: 'your-api-key',
  caching: true
});

// Generate fingerprint
const fingerprint = await tracetail.generateFingerprint({
  verbose: true
});

// Access fingerprint data
console.log('Visitor ID:', fingerprint.visitorId);
console.log('Confidence:', fingerprint.confidence);
console.log('Processing Time:', fingerprint.processingTime + 'ms');`;
    codeExampleContainer.innerHTML = '';
    codeExampleContainer.appendChild(createCodeBlock(exampleCode, 'javascript', 'Implementation Code'));
  }
});
